import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Zap } from 'lucide-react';
import { useLanguage } from './LanguageContext';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dmEeVyCNLLsP1vKt5zN2GP/logo_de_la_ruta.jpg';
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section className="relative min-h-[90vh] flex items-center justify-center px-6 md:px-12 overflow-hidden bg-barber-black" data-id="element-110">
      {/* Lightning flash overlay */}
      <motion.div className="absolute inset-0 bg-white pointer-events-none z-20" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 0, 0, 0.8, 0, 0.4, 0, 0.15, 0]
    }} transition={{
      duration: 1.8,
      delay: 0.6,
      times: [0, 0.3, 0.35, 0.38, 0.45, 0.48, 0.55, 0.58, 1],
      ease: 'linear',
      repeat: Infinity,
      repeatDelay: 2
    }} data-id="element-111" />

      {/* Lightning bolt SVG decorations */}
      <motion.div className="absolute top-0 left-1/4 z-10 pointer-events-none" initial={{
      opacity: 0,
      y: -100
    }} animate={{
      opacity: [0, 1, 0.8, 0],
      y: [-100, 80, 80, 80]
    }} transition={{
      duration: 1.2,
      delay: 0.7,
      ease: 'easeOut',
      repeat: Infinity,
      repeatDelay: 2
    }} data-id="element-112">
        <Zap size={80} className="text-barber-gold/60 fill-barber-gold/30" data-id="element-113" />
      </motion.div>
      <motion.div className="absolute top-0 right-1/3 z-10 pointer-events-none" initial={{
      opacity: 0,
      y: -120
    }} animate={{
      opacity: [0, 1, 0.6, 0],
      y: [-120, 60, 60, 60]
    }} transition={{
      duration: 1.0,
      delay: 0.9,
      repeat: Infinity,
      repeatDelay: 2,
      ease: 'easeOut'
    }} data-id="element-114">
        <Zap size={50} className="text-yellow-400/50 fill-yellow-400/20" data-id="element-115" />
      </motion.div>

      {/* Subtle background pattern/overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-116">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-barber-gold/20 via-transparent to-transparent" data-id="element-117" />
      </div>

      <motion.div className="max-w-5xl mx-auto text-center relative z-10 pt-20" animate={{
      x: [0, 0, 0, -4, 5, -3, 4, -2, 0],
      y: [0, 0, 0, 2, -3, 2, -1, 1, 0]
    }} transition={{
      duration: 0.8,
      delay: 1.0,
      ease: 'easeOut',
      repeat: Infinity,
      repeatDelay: 2
    }} data-id="element-118">
        {/* Logo */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7
      }} className="mb-8" data-id="element-119">
          <img src={LOGO_URL} alt="JP Barber Mobile Supply" className="w-full max-w-md md:max-w-lg object-contain mx-auto" data-id="element-120" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-barber-dark border border-barber-gold/30 text-barber-gold mb-8 text-sm font-medium tracking-wider uppercase" data-id="element-121">
          <Truck size={16} data-id="element-122" />
          <span data-id="element-123">{t.hero.badge}</span>
        </motion.div>

        {/* HEADLINE — Lightning strike animation */}
        <div className="mb-8 overflow-hidden" data-id="element-124">
          {/* Line 1 */}
          <motion.div initial={{
          opacity: 0,
          y: -300,
          scale: 1.3,
          rotateX: 25
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotateX: 0
        }} transition={{
          duration: 0.5,
          delay: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-5xl md:text-7xl font-bold font-oswald leading-tight" data-id="element-125">
            <motion.span animate={{
            textShadow: ['0 0 0px transparent', '0 0 30px rgba(212,168,83,0.8)', '0 0 60px rgba(212,168,83,0.4)', '0 0 10px rgba(212,168,83,0.2)', '0 0 0px transparent']
          }} transition={{
            duration: 1.5,
            delay: 1.0
          }} data-id="element-126">
              {t.hero.title1}
            </motion.span>
          </motion.div>

          {/* Line 2 — Gold text, slightly delayed */}
          <motion.div initial={{
          opacity: 0,
          y: -400,
          scale: 1.5,
          rotateX: 30
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotateX: 0
        }} transition={{
          duration: 0.45,
          delay: 1.05,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-5xl md:text-7xl font-bold font-oswald leading-tight text-barber-gold" data-id="element-127">
            <motion.span animate={{
            textShadow: ['0 0 0px transparent', '0 0 40px rgba(212,168,83,1)', '0 0 80px rgba(212,168,83,0.6)', '0 0 20px rgba(212,168,83,0.3)', '0 0 5px rgba(212,168,83,0.1)']
          }} transition={{
            duration: 2,
            delay: 1.2
          }} data-id="element-128">
              ⚡ {t.hero.title2} ⚡
            </motion.span>
          </motion.div>

          {/* Line 3 */}
          <motion.div initial={{
          opacity: 0,
          y: -350,
          scale: 1.4,
          rotateX: 28
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotateX: 0
        }} transition={{
          duration: 0.4,
          delay: 1.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-5xl md:text-7xl font-bold font-oswald leading-tight" data-id="element-129">
            <motion.span animate={{
            textShadow: ['0 0 0px transparent', '0 0 25px rgba(212,168,83,0.6)', '0 0 0px transparent']
          }} transition={{
            duration: 1.2,
            delay: 1.4
          }} data-id="element-130">
              {t.hero.title3}
            </motion.span>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1.8
      }} className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-131">
          {t.hero.subtitle1}
          <br className="hidden md:block" data-id="element-132" />
          <span className="text-lg md:text-xl text-gray-400 mt-4 block" data-id="element-133">
            {t.hero.subtitle2}
          </span>
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 2.1
      }} data-id="element-134">
          <a href="#contacto" className="inline-block bg-barber-gold hover:bg-barber-gold-hover text-barber-black font-oswald font-bold text-xl px-12 py-5 rounded transition-colors duration-300 uppercase tracking-wide" data-id="element-135">
            {t.hero.cta}
          </a>
        </motion.div>
      </motion.div>
    </section>;
}